import { Component, JSXElement, createSignal, Show, untrack } from 'solid-js';
import ProgressBar from '../ProgressBar/ProgressBar';

export interface ProgressWidgetProps {
  /** Widget title */
  title?: string;
  /** Icon class for the widget header */
  icon?: string;
  /** Progress value as percentage (0-100) */
  progress?: number;
  /** Progress value as string (e.g., "45%") */
  progressPercent?: string;
  /** Content to display in the widget body */
  content?: JSXElement;
  /** Whether the widget is collapsed */
  collapsed?: boolean;
  /** Callback when collapse state changes */
  onCollapseChange?(collapsed: boolean): void;
  /** Whether to show the progress bar */
  showProgress?: boolean;
  /** Additional CSS classes for the widget */
  class?: string;
  /** Additional CSS classes for the progress bar */
  progressClass?: string;
}

const ProgressWidget: Component<ProgressWidgetProps> = (props) => {
  const collapsedValue = untrack(() => props.collapsed) || false;
  const [isCollapsed, setIsCollapsed] = createSignal(collapsedValue);

  const getTitle = (): string => props.title || 'Progress Widget';
  const getIcon = (): string => props.icon || 'tc-icon-file-upload';
  const getContent = (): JSXElement | undefined => props.content;
  const getShowProgress = (): boolean => props.showProgress !== false;
  const getWidgetClass = (): string => {
    const baseClass = 'progress-widget';
    const collapsedClass = isCollapsed() ? 'collapsed' : 'expanded';
    const additionalClass = props.class ? ` ${props.class}` : '';
    return `${baseClass} ${collapsedClass}${additionalClass}`;
  };

  const handleCollapseToggle = (): void => {
    const newCollapsed = !isCollapsed();
    setIsCollapsed(newCollapsed);
    props.onCollapseChange?.(newCollapsed);
  };

  const getCollapseIcon = (): string =>
    isCollapsed() ? 'tc-icon-chevron-down' : 'tc-icon-chevron-up';

  return (
    <div class={getWidgetClass()}>
      <header class="pl-2 pr-1">
        <div class="progress-type mr-1">
          <i class={`icon-font i32 ${getIcon()} low`} />
        </div>
        <div class="title text-ellipsis">
          <h4 class="text-semibold text-ellipsis">{getTitle()}</h4>
        </div>
        <button
          class="button default hollow icon-medium icon-circle"
          onClick={handleCollapseToggle}
          aria-label={isCollapsed() ? 'Expand' : 'Collapse'}
        >
          <i class={`icon-font ${getCollapseIcon()}`} />
        </button>
      </header>

      <section>
        {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
        <div class="px-2 py-2">{getContent() || <p>Content goes here</p>}</div>
      </section>

      <Show when={getShowProgress()}>
        <ProgressBar
          progress={props.progress}
          progressPercent={props.progressPercent}
          class={props.progressClass}
          show={true}
          inFooter={true}
        />
      </Show>
    </div>
  );
};

export default ProgressWidget;
